import type { EditableCompanyProfile } from "@/lib/cat/company-profile.types";
import { formatAddressLinesForDisplay } from "@/lib/cat/mdm-address-line-uses";
import {
  formatPhoneUseLabel,
  parseMdmContactUse,
} from "@/lib/cat/mdm-map-contacts";
import type { Draft } from "@/prisma/generated/client";

export type RecapField = {
  label: string;
  value: string;
};

function display(value: string | null | undefined): string {
  const t = value?.trim();
  return t && t.length > 0 ? t : "—";
}

function phoneLine(
  label: string,
  value: string | null | undefined,
  use: string | null | undefined,
): RecapField | null {
  const v = value?.trim();
  if (!v) return null;
  const useNorm = parseMdmContactUse(use);
  return {
    label,
    value: useNorm === "unknown" ? v : `${v} (${formatPhoneUseLabel(useNorm)})`,
  };
}

function addressFields(prefix: {
  line: string | null | undefined;
  floor: string | null | undefined;
  building: string | null | undefined;
  postBox: string | null | undefined;
  postal: string | null | undefined;
  city: string | null | undefined;
}): RecapField[] {
  const parsed = {
    addressLine: prefix.line?.trim() ?? "",
    addressFloorApartment: prefix.floor?.trim() ?? "",
    addressBuildingResidence: prefix.building?.trim() ?? "",
    postBox: prefix.postBox?.trim() ?? "",
  };
  const lines = formatAddressLinesForDisplay(parsed);
  const fields: RecapField[] = [];

  if (lines[0]) fields.push({ label: "Voie", value: lines[0] });
  if (lines[1]) {
    fields.push({ label: "Étage, porte, appartement", value: lines[1] });
  }
  if (lines[2]) {
    fields.push({
      label: "Escalier, bâtiment, résidence",
      value: lines[2],
    });
  }
  if (lines[3]) fields.push({ label: "Boîte postale", value: lines[3] });

  const locality = [prefix.postal?.trim(), prefix.city?.trim()]
    .filter(Boolean)
    .join(" ");
  if (locality) {
    fields.push({ label: "Code postal / ville", value: locality });
  }

  return fields;
}

export function buildIndividualRecapFromDraft(
  draft: Draft | null,
): RecapField[] {
  if (!draft) return [];

  const fields: RecapField[] = [
    { label: "Nom", value: display(draft.clientLastName) },
    { label: "Prénom", value: display(draft.clientFirstName) },
    ...addressFields({
      line: draft.clientAddressLine,
      floor: draft.clientAddressFloorApartment,
      building: draft.clientAddressBuildingResidence,
      postBox: draft.clientPostBox,
      postal: draft.clientPostalCode,
      city: draft.clientCity,
    }),
    { label: "Email", value: display(draft.clientEmail) },
  ];

  const fixed = phoneLine(
    "Téléphone fixe",
    draft.clientFixedPhone,
    draft.clientFixedPhoneUse,
  );
  const mobile = phoneLine(
    "Téléphone mobile",
    draft.clientMobilePhone,
    draft.clientMobilePhoneUse,
  );
  if (fixed) fields.push(fixed);
  if (mobile) fields.push(mobile);

  return fields;
}

export function buildCompanyRecapFromDraft(
  draft: Draft | null,
  options?: { directorSale?: boolean },
): RecapField[] {
  if (!draft) return [];

  const fields: RecapField[] = [
    { label: "SIRET", value: display(draft.siretInput) },
    {
      label: "Raison sociale",
      value: display(draft.companyDenomination),
    },
  ];

  if (options?.directorSale) {
    return fields;
  }

  fields.push(
    { label: "Code APE", value: display(draft.companyApeCode) },
    ...addressFields({
      line: draft.companyAddressLine,
      floor: draft.companyAddressFloorApartment,
      building: draft.companyAddressBuildingResidence,
      postBox: draft.companyPostBox,
      postal: draft.companyPostalCode,
      city: draft.companyCity,
    }),
    { label: "Email", value: display(draft.companyEmail) },
  );

  const fixed = phoneLine(
    "Téléphone fixe",
    draft.companyFixedPhone,
    draft.companyFixedPhoneUse,
  );
  const mobile = phoneLine(
    "Téléphone mobile",
    draft.companyMobilePhone,
    draft.companyMobilePhoneUse,
  );
  if (fixed) fields.push(fixed);
  if (mobile) fields.push(mobile);

  return fields;
}

export function getClientRecapTypeLabel(draft: Draft | null): string {
  if (!draft) return "—";
  if (draft.rentSale) return "RENT";
  if (draft.directorSale) return "Vente à dirigeant";
  if (draft.exclusiveKind === "company") return "Société";
  if (draft.exclusiveKind === "individual") return "Particulier";
  return "—";
}

export function getIndividualRecapSectionTitle(draft: Draft | null): string {
  if (draft?.directorSale) return "Dirigeant";
  return "Particulier";
}

export function shouldShowClientRecap(draft: Draft | null): boolean {
  return shouldShowCompanyRecap(draft) || shouldShowIndividualRecap(draft);
}

export function draftToEditableCompanyProfile(
  draft: Draft | null,
): EditableCompanyProfile | null {
  if (!draft?.companyDenomination?.trim() && !draft?.siretInput?.trim()) {
    return null;
  }
  return {
    companyDenomination: draft.companyDenomination?.trim() ?? "",
    apeCode: draft.companyApeCode?.trim() ?? "",
    email: draft.companyEmail?.trim() ?? "",
    addressLine: draft.companyAddressLine?.trim() ?? "",
    addressFloorApartment: draft.companyAddressFloorApartment?.trim() ?? "",
    addressBuildingResidence:
      draft.companyAddressBuildingResidence?.trim() ?? "",
    postBox: draft.companyPostBox?.trim() ?? "",
    postalCode: draft.companyPostalCode?.trim() ?? "",
    city: draft.companyCity?.trim() ?? "",
    fixedPhone: {
      value: draft.companyFixedPhone?.trim() ?? "",
      use:
        draft.companyFixedPhoneUse?.trim().toLowerCase() === "work"
          ? "work"
          : "home",
    },
    mobilePhone: {
      value: draft.companyMobilePhone?.trim() ?? "",
      use:
        draft.companyMobilePhoneUse?.trim().toLowerCase() === "work"
          ? "work"
          : "home",
    },
  };
}

export function draftToEditableIndividualProfile(
  draft: Draft | null,
):
  | import("@/lib/cat/individual-profile.types").EditableIndividualProfile
  | null {
  if (!draft?.clientLastName?.trim() && !draft?.clientEmail?.trim()) {
    return null;
  }
  return {
    email: draft.clientEmail?.trim() ?? "",
    addressLine: draft.clientAddressLine?.trim() ?? "",
    addressFloorApartment: draft.clientAddressFloorApartment?.trim() ?? "",
    addressBuildingResidence:
      draft.clientAddressBuildingResidence?.trim() ?? "",
    postBox: draft.clientPostBox?.trim() ?? "",
    postalCode: draft.clientPostalCode?.trim() ?? "",
    city: draft.clientCity?.trim() ?? "",
    fixedPhone: {
      value: draft.clientFixedPhone?.trim() ?? "",
      use: parseMdmContactUse(draft.clientFixedPhoneUse),
    },
    mobilePhone: {
      value: draft.clientMobilePhone?.trim() ?? "",
      use: parseMdmContactUse(draft.clientMobilePhoneUse),
    },
  };
}

export function shouldShowCompanyRecap(draft: Draft | null): boolean {
  if (!draft) return false;
  return Boolean(
    draft.rentSale || draft.directorSale || draft.exclusiveKind === "company",
  );
}

export function shouldShowIndividualRecap(draft: Draft | null): boolean {
  if (!draft || draft.rentSale) return false;
  return Boolean(draft.directorSale || draft.exclusiveKind === "individual");
}
